import type { FieldErrors } from 'react-hook-form';
import { get, omit } from 'lodash-es';
import { IncomeType } from '@zg-rentals/ts-capps-client';
import type { Answers, AnswersInput, CurrentIncomeInput, Dog } from '../models/rental-application';
import { filteredKeys, topLevelFilteredKeys } from '../constants/applicationsConstants';
import { hasDogDetails } from './capps-mappers';

export const EMPTY_DOG: Dog = { weightLbs: null, breed: null };

export function withEmptyDog(answers: Answers): Answers {
  return answers.pets?.dogs?.length ? answers : { ...answers, pets: { ...answers.pets, dogs: [EMPTY_DOG] } };
}

export function getAnswersInput(answers: Answers, initialAnswers: Answers): AnswersInput {
  const filteredAnswers = omit(
    filterAnswersForSubmit(fillAnswerDefaults(answers, initialAnswers)),
    topLevelFilteredKeys,
  );
  const filteredInitialAnswers = omit(filterAnswersForSubmit(initialAnswers), topLevelFilteredKeys);

  // Only submit answers that have changed
  return Object.fromEntries(
    Object.entries(filteredAnswers).filter(
      ([key, value]) => JSON.stringify(filteredInitialAnswers[key as keyof AnswersInput]) !== JSON.stringify(value),
    ),
  );
}

function fillAnswerDefaults(answers: Answers, initialAnswers: Answers): Answers {
  return {
    ...answers,
    // The add/remove document mutations require an incomeId, so we need to
    // add a default income if there are none
    currentIncomes: !answers.currentIncomes?.length
      ? [
          {
            type: IncomeType.EMPLOYMENT,
          } satisfies CurrentIncomeInput,
        ]
      : answers.currentIncomes,
    pets: fillPetsDefaults(answers.pets, initialAnswers.pets),
  };
}

function fillPetsDefaults(pets: Answers['pets'], initialPets: Answers['pets']): Answers['pets'] {
  if (!pets?.dogs) {
    return pets;
  }
  const dogs = pets.dogs.filter(hasDogDetails);
  const isUnansweredSeed =
    initialPets == null &&
    !dogs.length &&
    Object.values(omit(pets, 'dogs')).every((value) => value == null || value === '');
  return isUnansweredSeed ? initialPets : { ...pets, dogs };
}

// Remove keys not in AnswersInput as they are not accepted by saveAnswers
function filterAnswersForSubmit(answers: Answers): AnswersInput {
  if (!answers || typeof answers !== 'object') {
    return answers;
  }
  if (Array.isArray(answers)) {
    return answers.map(filterAnswersForSubmit) as AnswersInput;
  }
  return Object.fromEntries(
    Object.entries(answers)
      .filter(([key]) => !filteredKeys.has(key))
      .map(([key, value]) => {
        if (typeof value === 'object') {
          if (key === 'documents') {
            // We don't send documents to the saveAnswers mutation
            // since these are handled by the addDocumentMutation and the removeDocumentMutation
            return [key, null];
          }
          return [key, filterAnswersForSubmit(answers[key as keyof Answers] as Answers)];
        }

        return [key, typeof value === 'string' && value.trim() === '' ? null : value];
      }),
  );
}

export function getAnswersInputWithoutInvalid(
  answers: AnswersInput | Answers,
  initialAnswers?: Answers,
  errors?: FieldErrors<Answers>,
): AnswersInput {
  if (!answers || typeof answers !== 'object') {
    return answers;
  }
  if (Array.isArray(answers)) {
    return answers.map((answer, index) =>
      getAnswersInputWithoutInvalid(
        answer,
        (initialAnswers as Array<Answers> | undefined)?.[index],
        errors?.[index.toString() as keyof Answers],
      ),
    ) as AnswersInput;
  }
  return Object.fromEntries(
    Object.entries(answers).map(([key, value]) => {
      const fieldId = key as keyof AnswersInput;

      if (value && typeof value === 'object') {
        // recursion
        return [
          key,
          getAnswersInputWithoutInvalid(
            value as AnswersInput,
            initialAnswers ? (initialAnswers[fieldId] as Answers) : undefined,
            errors ? errors[fieldId] : undefined,
          ),
        ];
      }

      if (errors && errors[fieldId]) {
        return [key, initialAnswers ? (initialAnswers[fieldId] as AnswersInput) : null];
      }

      return [key, typeof value === 'string' && value.trim() === '' ? null : value];
    }),
  );
}

/**
 * Whether a field name from saveAnswers addresses a single form input, so that setError paints it.
 * The api answers with relative names, e.g. 'amount' for the amount of an unspecified income and
 * 'documents' for an income document, and those cannot be matched to an input
 */
export function isFormInput(answers: Answers, fieldName: string) {
  const value = get(answers, fieldName);
  return value === null || (value !== undefined && typeof value !== 'object');
}

export function getErrorsWithoutRequired(errors?: FieldErrors<Answers>): FieldErrors<Answers> {
  if (!errors) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(errors)
      .filter(([_, error]) => error != null && (!('type' in error) || error.type !== 'required'))
      .map(([fieldId, error]) => {
        // recursion for nested error objects like currentResidence.address.zip
        if (!('type' in error)) {
          return [fieldId, getErrorsWithoutRequired(errors[fieldId as keyof Answers])];
        }

        return [fieldId, error];
      })
      .filter(([_, error]) => Object.entries(error || {}).length > 0),
  );
}
